/**
 * The Scheduling board as a Kanban (Isaiah Cotto, 2026-10-13): the operational
 * kit (components/board/kanban) over GET /scheduling/board, one request per
 * eMerge state, plus GET /scheduling/board/completed-visits for the history column.
 *
 * A state card is a BUILDING. The server places it: each state column is one state tab
 * of the board (`tab=<state>`), its count is that tab's count, and a card's
 * `column` is the row's own `emerge_state.key`. Nothing here derives a state,
 * and nothing is coloured by the passing of time. Monday's columns are not on a
 * card; the reference tabs are the Elsewhere strip.
 *
 * == Columns (Isaiah Cotto, 2026-10-14) ======================================
 *   Needs a visit plan · Planned · Confirmed · Upcoming · Blocked · Closed  |  Completed visits, last 30 days
 * The six STATE columns are mutually exclusive and add up to Main table. Upcoming is the board's one named
 * time-derived state (schedulingBoard.js header): a confirmed next visit dated today through today + 14 (Florida
 * day), decided by the SERVER. It sits right of Confirmed because that is the way a building moves as time passes:
 * planned, confirmed, then upcoming as its date comes near -- never backward -- and the two confirmed columns stay
 * side by side. Blocked and Closed stay last, as before: they are off the visit's path.
 * Completed is HISTORY, not a state: one card per completed visit (the card's id is the plan's), apart at the far
 * right so it is never read as part of the sum. A building may be there AND in its current state's column. Its cards
 * do not drag, nothing drops on it, its Move… menu is empty, and a card opens the BUILDING's dock (detail.recordOf).
 *
 * == Drops (SCHEDULING_TRANSITIONS) ========================================
 * A drop never writes. It opens the existing governed act in the docked
 * facility panel (FacilityPanelBody), with that panel's own form, checks and
 * confirm; the server decides again, and every act carries the plan's version.
 *
 *   No open plan -> Planned            opens "Schedule a visit"
 *   Planned -> Confirmed or Upcoming   opens "Confirm" (the visit's date decides which of the two it lands in); with
 *                                      no provider on the visit, "Assign provider" opens instead and says why (the
 *                                      server refuses PROVIDER_REQUIRED)
 *   Planned / Confirmed / Upcoming -> No plan   opens "Cancel visit" (a reason is required; the building falls to its next open plan, if any)
 *   No plan / Planned / Confirmed / Upcoming -> Blocked   opens "Record blocker" (a reason is required)
 *   Blocked -> any state               opens "Clear blocker" only; the server decides which state the building returns to
 *   Confirmed / Upcoming -> Planned    refused: there is no un-confirm
 *   Confirmed <-> Upcoming             refused: the visit date decides; reschedule to move it
 *   to or from Closed                  refused: no act moves a building in or out of Closed. A closed card does not drag;
 *                                      its Move… offers only Cancel visit and Complete visit for a visit already planned
 *                                      there (Isaiah Cotto, 2026-10-14: cleanup; the server refuses every other act)
 *   to or from Completed visits        refused: history; a visit is completed from its building (Complete visit)
 *
 * "Reschedule" and "Complete visit" are not column moves: they are in the card's Move… menu
 * (TransitionMap.inPlace, never a drag) and the panel's own buttons. Upcoming takes them exactly as Confirmed does.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { OperationalKanban, LayoutSwitch, CardChips } from '../../components/board/kanban';
import type { KanbanColumnData, KanbanChip, TransitionMap, Transition, ActTransition, ElsewhereBucket } from '../../components/board/kanban';
import { useBoardUrlState, boardDate } from '../../components/board';
import { schedulingApi, outcomeOf } from '../finance/financeApi';
import type { ProviderOption } from '../finance/financeApi';
import { schedulingBoardApi } from './boardApi';
import type { SchedulingBoardPage, SchedulingBoardRow, EmergeStateKey, CompletedVisit } from './boardApi';
import { FacilityPanelBody } from './FacilityOperations';
import type { Command } from './FacilityOperations';
import { AddBuildingButton, useBuildingMeta } from './BuildingEditor';

/** A building card: the board's row, with the state column the server placed it in. */
export type BuildingCard = SchedulingBoardRow & { column: EmergeStateKey };
/** A completed visit (history, 2026-10-14): the server's row; its id is the visit plan's, its record is the building. */
export type VisitCard = CompletedVisit & { column: 'completed' };
export type SchedCard = BuildingCard | VisitCard;
/** What a drop's availability check is given. */
export interface SchedKanbanCtx { canPlan: boolean }

export const isVisit = (c: SchedCard): c is VisitCard => c.kind === 'COMPLETED_VISIT';

/**
 * The state columns, left to right (Isaiah Cotto, 2026-10-13; Upcoming 2026-10-14, right of Confirmed: the way a
 * building moves as its date comes near). The keys are the server's `emerge_state.key`; together they are Main table.
 */
export const COLUMN_ORDER: EmergeStateKey[] = ['no-plan', 'planned', 'confirmed', 'upcoming', 'blocked', 'closed'];
/** The history column (2026-10-14), after the states and apart from them. */
export const COMPLETED_COLUMN = 'completed';
export const KANBAN_ORDER: string[] = [...COLUMN_ORDER, COMPLETED_COLUMN];
const COLUMN_LABEL: Record<EmergeStateKey, string> = { 'no-plan': 'Needs a visit plan', planned: 'Planned', confirmed: 'Confirmed', upcoming: 'Upcoming', blocked: 'Blocked', closed: 'Closed' };
const COMPLETED_LABEL = 'Completed visits, last 30 days';
const PAGE_SIZE = 50;
const READ_ONLY = 'Read-only: the visits write is not open for this account.';
const lower = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);
const buildingName = (c: SchedCard) => (c.facility && c.facility.name) || 'This building';
/** Every accessible name on a card. A visit card names its date too, so a building's history cards and its state card differ. */
const nameOf = (c: SchedCard) => (isVisit(c) ? `${buildingName(c)}, visit completed ${boardDate(c.completed_on) || c.completed_on}` : buildingName(c));

function actEntry(act: Command, label: string, opens: string, needs: 'none' | 'plan' | 'blocker' = 'none'): Transition<SchedCard, SchedKanbanCtx> {
  return {
    kind: 'act', act, label, opens,
    available: (card, ctx) => {
      if (isVisit(card)) return COMPLETED_FROM;
      if (!ctx.canPlan) return READ_ONLY;
      if (needs === 'plan' && !card.next_plan) return 'This building has no open visit plan to act on. Open it to see why.';
      if (needs === 'blocker' && !card.blocker) return 'This building has no open blocker to clear. Open it to see its state.';
      return true;
    },
  };
}

const COMPLETED_FROM = 'A completed visit is history: nothing moves out of Completed visits. Open the building to act on its current visit.';
const COMPLETED_TO = 'Completed visits is history, not a column a building is moved to. Complete a visit from the building (Move… or its panel); it is listed here once recorded.';
const DATE_DECIDES = 'The visit date decides between Upcoming and Confirmed; reschedule to move it.';
const SCHEDULE = actEntry('schedule', 'Schedule a visit…', 'Opens Schedule a visit in the panel (a date that is not in the past is required; a provider is optional)');
const noProvider = (card: SchedCard) => Boolean(!isVisit(card) && card.next_plan && !card.next_plan.provider_employee_id);
/**
 * Confirm, or -- when the visit has no provider and the server would refuse PROVIDER_REQUIRED -- Assign provider first.
 * Said exactly, by the card's own plan. The same act for a drop on Confirmed or on Upcoming: the visit's date, not the
 * column, decides which of the two the building lands in, and the server decides it.
 */
const CONFIRM: Transition<SchedCard, SchedKanbanCtx> = {
  ...(actEntry('confirm', '', '', 'plan') as ActTransition<SchedCard, SchedKanbanCtx>),
  act: (card) => (noProvider(card) ? 'provider' : 'confirm'),
  label: (card) => (noProvider(card) ? 'Assign provider, then confirm…' : 'Confirm…'),
  opens: (card) => (noProvider(card)
    ? 'Opens Assign provider in the panel: the visit has no provider, and the server will not confirm without one. Confirm comes after'
    : 'Opens Confirm in the panel. The visit\'s date decides whether it lands in Upcoming (the next 14 days) or Confirmed'),
};
const CANCEL = actEntry('cancel', 'Cancel visit…', 'Opens Cancel visit in the panel (a reason is required; cancelling falls to the next open plan, if there is one)', 'plan');
const BLOCKER = actEntry('blocker', 'Record blocker…', 'Opens Record blocker in the panel (a reason is required)');
const clearTo = (to: EmergeStateKey) => actEntry('clear', 'Clear blocker…',
  `Opens Clear blocker in the panel. The building returns to its underlying state, which the server decides, so not necessarily ${lower(COLUMN_LABEL[to])}`, 'blocker');

const COMPLETE_IN_PLACE = { act: 'complete', label: 'Complete visit…', opens: 'Opens Complete visit in the panel: Scheduling\'s record that the visit happened, not the clinical visit', available: true as const };
/** Cancel without leaving Closed (a closed building has no column to fall to): cleanup of a visit planned before the closure. */
const CLOSED_CANCEL = { act: 'cancel', label: 'Cancel visit…', opens: 'Opens Cancel visit in the panel (a reason is required): cleanup of a visit planned before the building closed', available: true as const };
/** The dock's words for a closed building (2026-10-14). */
export const closedNote = (kind: string) => `Closed for scheduling (${kind}): only cancelling or completing a visit already planned here.`;
const NOTHING_PLANNED: Transition<SchedCard, SchedKanbanCtx> = { kind: 'refuse', reason: 'Nothing is planned yet. A visit is scheduled first, then confirmed.', instead: { label: 'Schedule a visit…', act: 'schedule' } };
const NO_UNCONFIRM: Transition<SchedCard, SchedKanbanCtx> = { kind: 'refuse', reason: 'A confirmed visit cannot be un-confirmed. Reschedule it to change the date, or cancel it.', instead: { label: 'Reschedule…', act: 'reschedule' } };
const BY_DATE: Transition<SchedCard, SchedKanbanCtx> = { kind: 'refuse', reason: DATE_DECIDES, instead: { label: 'Reschedule…', act: 'reschedule' } };

export const SCHEDULING_TRANSITIONS: TransitionMap<SchedCard, SchedKanbanCtx> = {
  moves: {
    'no-plan': { planned: SCHEDULE, blocked: BLOCKER, confirmed: NOTHING_PLANNED, upcoming: NOTHING_PLANNED },
    // A drop on Upcoming opens Confirm too; Move… lists Confirm once (inMenu: false), because the date, not the item, decides.
    planned: { confirmed: CONFIRM, upcoming: { ...(CONFIRM as ActTransition<SchedCard, SchedKanbanCtx>), inMenu: false }, 'no-plan': CANCEL, blocked: BLOCKER },
    // Upcoming behaves exactly like Confirmed (Isaiah Cotto, 2026-10-14); between the two, only the date moves a building.
    confirmed: { 'no-plan': CANCEL, blocked: BLOCKER, planned: NO_UNCONFIRM, upcoming: BY_DATE },
    upcoming: { 'no-plan': CANCEL, blocked: BLOCKER, planned: NO_UNCONFIRM, confirmed: BY_DATE },
    blocked: { 'no-plan': clearTo('no-plan'), planned: clearTo('planned'), confirmed: clearTo('confirmed'), upcoming: clearTo('upcoming') },
  },
  // Reschedule and Complete visit leave the card where it is: Move… offers them (never a drag), for a building with a plan,
  // and never on a completed visit (history: its Move… is empty).
  // A CLOSED building (Isaiah Cotto, 2026-10-14) takes only the cleanup of a visit already planned there: Cancel visit and
  // Complete visit. The server refuses every other act on a closed building; these two it still allows.
  inPlace: (card, ctx) => {
    if (isVisit(card) || !ctx.canPlan || !card.next_plan) return [];
    if (card.column === 'closed') return [CLOSED_CANCEL, COMPLETE_IN_PLACE];
    return [{ act: 'reschedule', label: 'Reschedule…', opens: 'Opens Reschedule in the panel (a new date is required)', available: true as const }, COMPLETE_IN_PLACE];
  },
  // A closed building and a completed visit have nothing a drag could open: no grip, so nobody is offered a move that is always refused.
  canDrag: (card) => card.column !== 'closed' && !isVisit(card),
  fallbackReason: (from, to) => {
    if (from === COMPLETED_COLUMN) return COMPLETED_FROM;
    if (to === COMPLETED_COLUMN) return COMPLETED_TO;
    if (from === 'closed') return 'Closed buildings are read-only on the board: no act reopens one.';
    if (to === 'closed') return 'Closing a building is not a move on the board: no act sends a building to Closed.';
    if (from === 'blocked') return 'A blocked building is only unblocked: clear its blocker, and the server decides where it returns.';
    return 'No single act makes that move. Open the building to see what it can take.';
  },
};

/** What a completed visit card says: the building, the day it was completed, the provider, the purpose if any. History only. */
export function CompletedVisitBody({ card }: { card: VisitCard }) {
  return (
    <>
      <span className="okb-c-name">{buildingName(card)}</span>
      <span className="okb-c-row"><span className="okb-c-next">Visit completed</span><span className="okb-c-due">{boardDate(card.completed_on) || card.completed_on}</span></span>
      <span className="okb-c-sub">{card.provider ? (card.provider.name || 'Provider name not recorded') : 'No provider recorded on the visit'}</span>
      {card.purpose && <span className="okb-c-sub" title={card.purpose}>{card.purpose}</span>}
    </>
  );
}

/** What a card says. Phrasing content only: the card's body is the button that opens the building. No resident, no Monday field. */
/** The open building at a glance, under its name in the dock (2026-10-30): where it is, its blocker, its coverage. */
export function SchedulingDockMeta({ card }: { card: BuildingCard }) {
  const where = card.facility ? [card.facility.city, card.facility.region].filter(Boolean).join(' · ') : '';
  const names = card.coverage.map((c) => c.name || 'Name not recorded');
  const covered = names.length ? `${names.slice(0, 2).join(', ')}${names.length > 2 ? ` +${names.length - 2}` : ''}` : null;
  return (
    <>
      <span className="board-dock__metaitem">{where || 'City and region not recorded'}</span>
      {card.blocker && <span className="okb-chips"><span className="okb-chip okb-chip--stop" title={card.blocker.reason}><span className="okb-sr">Blocking: </span>{card.blocker.reason}</span></span>}
      <span className={`board-dock__metaitem okb-c-owner${covered ? '' : ' okb-c-owner--none'}`}>{covered ? `Covered by ${covered}` : 'No active coverage'}</span>
    </>
  );
}

export function SchedulingCardBody({ card }: { card: SchedCard }) {
  if (isVisit(card)) return <CompletedVisitBody card={card} />;
  const where = card.facility ? [card.facility.city, card.facility.region].filter(Boolean).join(' · ') : '';
  const next = card.next_visit;
  const closed = card.column === 'closed';
  const last = card.last_visit && card.last_visit.value ? card.last_visit.value : null;
  const names = card.coverage.map((c) => c.name || 'Name not recorded');
  const covered = names.length ? `${names.slice(0, 2).join(', ')}${names.length > 2 ? ` +${names.length - 2}` : ''}` : null;
  const chips: KanbanChip[] = [];
  if (card.expedited_open > 0) chips.push({ key: 'expedited', label: `${card.expedited_open} expedited open`, tone: 'warn' });
  chips.push(card.manifest_link
    ? { key: 'manifest', label: 'Manifest linked', tone: 'info' }
    : { key: 'manifest', label: card.manifest_link_state === 'MONDAY_REFERENCE_ONLY' ? 'Manifest: Monday reference only' : 'No manifest link', tone: 'neutral' });
  return (
    <>
      <span className="okb-c-name">{nameOf(card)}</span>
      <span className="okb-c-sub">{where || 'City and region not recorded'}</span>
      {card.blocker && (
        <span className="okb-chips"><span className="okb-chip okb-chip--stop" title={card.blocker.reason}><span className="okb-sr">Blocking: </span>{card.blocker.reason}</span></span>
      )}
      {closed ? (
        <>
          <span className="okb-c-row"><span className="okb-c-next">Closed for scheduling</span><span className="okb-c-due">{closureWords(card)}</span></span>
          {card.profile && card.profile.closure && card.profile.closure.reason && <span className="okb-c-sub" title={card.profile.closure.reason}>{card.profile.closure.reason}</span>}
          {next && <span className="okb-c-sub">Open plan left on a closed building: {boardDate(next.on) || next.on} ({next.state.toLowerCase()})</span>}
        </>
      ) : (
        <>
          <span className="okb-c-row">
            <span className="okb-c-next">Next visit</span>
            {next ? <span className="okb-c-due">{boardDate(next.on) || next.on}</span> : <span className="okb-c-due">None planned</span>}
          </span>
          {next && <span className="okb-c-sub">{next.provider ? (next.provider.name || 'Provider name not recorded') : 'No provider on the visit yet'}</span>}
        </>
      )}
      <span className="okb-c-sub">{last ? `Last visit ${boardDate(last.on) || last.on}` : 'No last visit recorded'}</span>
      <span className="okb-c-sub" title={names.join(', ') || undefined}>{covered ? `Covered by ${covered}` : 'No active coverage'}</span>
      <CardChips chips={chips} />
    </>
  );
}

const CLOSURE_WORDS: Record<string, string> = { DROPPED: 'dropped', DO_NOT_SCHEDULE: 'do not schedule' };
/** "dropped" / "do not schedule", from the building's scheduling profile (as the table's state cell does). */
const closureWords = (c: SchedCard) => (!isVisit(c) && c.profile && c.profile.closure ? (CLOSURE_WORDS[c.profile.closure.kind] || c.profile.closure.kind) : 'reason not recorded');
const COMMAND_LABEL: Record<Command, string> = { schedule: 'Schedule a visit', reschedule: 'Reschedule', provider: 'Assign provider', confirm: 'Confirm', complete: 'Complete visit', cancel: 'Cancel visit', details: 'Edit visit details', blocker: 'Record blocker', clear: 'Clear blocker' };

const SORT_LABEL: Record<string, string> = { facility: 'Building', 'next-visit': 'Next visit', 'last-visit': 'Last visit' };

type Columns = Array<KanbanColumnData<SchedCard> & { page: number }>;
interface Meta { tabs: SchedulingBoardPage['tabs']; options: SchedulingBoardPage['filter_options'] | null; sorts: string[]; canPlan: boolean | null }
interface Start { /** The card's id (the row's id: the building's id for a building row). */ facilityId: string; command: Command; nonce: number }

export interface SchedulingKanbanProps {
  scope: { can: { plan?: boolean } };
  onNotice: (msg: string) => void;
  /** The toolbar's Kanban | Table switch. Omitted (2026-10-14): the workspace sidebar's Views group chooses the layout. */
  onLayout?: (layout: 'kanban' | 'table') => void;
}

export default function SchedulingKanban({ scope, onNotice, onLayout }: SchedulingKanbanProps) {
  const [st, update] = useBoardUrlState(['provider', 'building']);
  const [, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const [columns, setColumns] = useState<Columns>([]);
  const [meta, setMeta] = useState<Meta>({ tabs: [], options: null, sorts: [], canPlan: null });
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState<ProviderOption[]>([]);
  const [start, setStart] = useState<Start | null>(null);
  /** Why the open form is the one it is (Assign provider instead of Confirm). Belongs to one building and to one opening. */
  const [why, setWhy] = useState<{ id: string; text: string } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** Columns that could not be read (left out, never shown as 0), or a load that spans two days: said in the status line. */
  const [readErr, setReadErr] = useState<string | null>(null);
  /**
   * The open building when it is not on any loaded page (a shared link, a refresh, the first-load window): its own state is read
   * (tab=all&building=<id>), and until that answers the panel is read-only. `found` carries the row, so closed is known.
   */
  const [lookup, setLookup] = useState<{ id: string; state: 'checking' | 'found' | 'missing'; card?: SchedCard } | null>(null);
  const buildingMeta = useBuildingMeta();
  const seq = useRef(0);
  const nonce = useRef(0);

  const canPlan = Boolean(meta.canPlan !== null ? meta.canPlan : scope.can.plan);
  const sortKey = st.sort && SORT_LABEL[st.sort] ? st.sort : null;
  const query = useCallback((tab: string, page: number) => ({ tab, q: st.q, provider: st.filters.provider, building: st.filters.building, sort: sortKey, dir: st.dir, page, limit: PAGE_SIZE }), [st.q, st.filters.provider, st.filters.building, sortKey, st.dir]);
  /** The history column's read: the same search and filters, its own order (newest first), never the board's sort. */
  const visitsQuery = useCallback((page: number) => ({ q: st.q, provider: st.filters.provider, building: st.filters.building, page, limit: PAGE_SIZE }), [st.q, st.filters.provider, st.filters.building]);

  /**
   * One load of the board (verification F4 / F6, 2026-10-14):
   *   - ONE day. Each read computes the server's Florida day on its own, so a load that crosses midnight could mix
   *     days (a building upcoming in one column's read and confirmed in another's). Every response says its `day`;
   *     if they differ, the whole board is read once more, and if they still differ the status line says so.
   *   - Per column. A column whose read fails (a rolled-back server answering TAB_UNKNOWN for tab=upcoming, a
   *     timeout) is LEFT OUT and named on the status line; the columns that were read are shown. Never a failed
   *     column as 0. Only when no state column was read is the board an error.
   */
  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true); setErr(null);
    try {
      const readAll = () => Promise.all([
        Promise.all(COLUMN_ORDER.map((k) => schedulingBoardApi.board(query(k, 1)).catch(() => null))),
        schedulingBoardApi.completedVisits(visitsQuery(1)).catch(() => null),
      ]);
      const daysOf = (rs: Array<Awaited<ReturnType<typeof schedulingBoardApi.board>> | null>, v: Awaited<ReturnType<typeof schedulingBoardApi.completedVisits>> | null) => new Set(
        [...rs.map((r) => (r && r.success && r.data ? r.data.day : null)), v && v.success && v.data ? v.data.day : null].filter((d): d is string => Boolean(d)),
      );
      let [rs, visits] = await readAll();
      if (mine !== seq.current) return;
      let days = daysOf(rs, visits);
      if (days.size > 1) {
        [rs, visits] = await readAll();
        if (mine !== seq.current) return;
        days = daysOf(rs, visits);
      }
      const read = COLUMN_ORDER.map((k, i) => ({ k, r: rs[i] })).filter((x) => x.r && x.r.success && x.r.data);
      if (!read.length) {
        const bad = rs.find((r) => r && !(r.success && r.data));
        setErr(bad ? outcomeOf(bad) : 'Could not reach the server, so nothing here is current.');
        return;
      }
      const first = read[0].r!.data!;
      setMeta({ tabs: first.tabs, options: first.filter_options, sorts: first.sorts, canPlan: first.can.plan });
      const states: Columns = read.map(({ k, r }) => {
        const d = r!.data!;
        const tab = d.tabs.find((t) => t.key === k);
        return {
          key: k, label: COLUMN_LABEL[k], what: tab ? tab.what : undefined, count: tab && typeof tab.count === 'number' ? tab.count : d.total,
          cards: d.items.map((x) => ({ ...x, column: x.emerge_state ? x.emerge_state.key : k })),
          has_more: d.page * d.limit < d.total, page: d.page,
        };
      });
      const missed: string[] = COLUMN_ORDER.map((k, i) => ({ k, r: rs[i] })).filter((x) => !(x.r && x.r.success && x.r.data))
        .map(({ k, r }) => `${COLUMN_LABEL[k]}${r ? ` (${outcomeOf(r)})` : ''}`);
      if (visits && visits.success && visits.data) {
        const v = visits.data;
        states.push({
          key: COMPLETED_COLUMN, label: COMPLETED_LABEL, what: v.what, note: 'History, not a building state', history: true, unit: ['visit', 'visits'],
          count: v.total, cards: v.items.map((x) => ({ ...x, column: COMPLETED_COLUMN as 'completed' })), has_more: v.page * v.limit < v.total, page: v.page,
        });
      } else {
        missed.push(`Completed visits${visits ? ` (${outcomeOf(visits)})` : ''}`);
      }
      const said: string[] = [];
      if (missed.length) said.push(`Could not be read, so not shown: ${missed.join('; ')}. The other columns are current.`);
      if (days.size > 1) said.push(`The columns were read on different days (${Array.from(days).sort().join(' and ')}: Florida midnight passed while loading). Reload to line them up.`);
      setReadErr(said.length ? said.join(' ') : null);
      setColumns(states);
    } catch { if (mine === seq.current) setErr('Could not reach the server, so nothing here is current.'); } finally { if (mine === seq.current) setLoading(false); }
  }, [query, visitsQuery]);
  useEffect(() => { load(); }, [load]);

  const loadMore = useCallback(async (key: string) => {
    const col = columns.find((c) => c.key === key);
    if (!col) return;
    // A search, filter or reload that lands while this page is in flight makes it stale: it is dropped (load() bumps the same sequence).
    const mine = seq.current;
    setColumns((cs) => cs.map((c) => (c.key === key ? { ...c, loading_more: true } : c)));
    if (key === COMPLETED_COLUMN) {
      try {
        const r = await schedulingBoardApi.completedVisits(visitsQuery(col.page + 1));
        if (mine !== seq.current) return;
        setColumns((cs) => cs.map((c) => {
          if (c.key !== key) return c;
          if (!(r.success && r.data)) return { ...c, loading_more: false };
          const d = r.data;
          const have = new Set(c.cards.map((k) => k.id));
          const more = d.items.filter((x) => !have.has(x.id)).map((x) => ({ ...x, column: COMPLETED_COLUMN as 'completed' }));
          return { ...c, loading_more: false, count: d.total, has_more: d.page * d.limit < d.total, page: d.page, cards: [...c.cards, ...more] };
        }));
        if (!(r.success && r.data)) setErr(outcomeOf(r));
      } catch {
        if (mine !== seq.current) return;
        setColumns((cs) => cs.map((c) => (c.key === key ? { ...c, loading_more: false } : c)));
        setErr('Could not reach the server.');
      }
      return;
    }
    try {
      const r = await schedulingBoardApi.board(query(key, col.page + 1));
      if (mine !== seq.current) return;
      setColumns((cs) => cs.map((c) => {
        if (c.key !== key) return c;
        if (!(r.success && r.data)) return { ...c, loading_more: false };
        const d = r.data;
        const have = new Set(c.cards.map((k) => k.id));
        const more = d.items.filter((x) => !have.has(x.id)).map((x) => ({ ...x, column: x.emerge_state ? x.emerge_state.key : (key as EmergeStateKey) }));
        return { ...c, loading_more: false, count: d.total, has_more: d.page * d.limit < d.total, page: d.page, cards: [...c.cards, ...more] };
      }));
      if (!(r.success && r.data)) setErr(outcomeOf(r));
    } catch {
      if (mine !== seq.current) return;
      setColumns((cs) => cs.map((c) => (c.key === key ? { ...c, loading_more: false } : c)));
      setErr('Could not reach the server.');
    }
  }, [columns, query, visitsQuery]);

  useEffect(() => {
    if (!canPlan) return;
    (async () => { try { const r = await schedulingApi.providers(); if (r.success && r.data) setProviders(r.data); } catch { /* the picker is empty; the server still refuses a bad id */ } })();
  }, [canPlan]);

  const ctx: SchedKanbanCtx = useMemo(() => ({ canPlan }), [canPlan]);
  const byId = useMemo(() => {
    const m = new Map<string, SchedCard>();
    columns.forEach((c) => c.cards.forEach((k) => m.set(k.id, k)));
    return m;
  }, [columns]);

  const openId = st.open;
  const loadedOpen = openId ? byId.has(openId) : true;
  useEffect(() => {
    if (!openId || loadedOpen) return undefined;
    let live = true;
    setLookup((l) => (l && l.id === openId ? l : { id: openId, state: 'checking' }));
    (async () => {
      try {
        const r = await schedulingBoardApi.board({ tab: 'all', building: openId, limit: 1 });
        if (!live) return;
        const row = r.success && r.data ? r.data.items.find((x) => x.id === openId || (x.facility && x.facility.id === openId)) : undefined;
        setLookup(row && row.emerge_state ? { id: openId, state: 'found', card: { ...row, column: row.emerge_state.key } as BuildingCard } : { id: openId, state: 'missing' });
      } catch { if (live) setLookup({ id: openId, state: 'missing' }); }
    })();
    return () => { live = false; };
  }, [openId, loadedOpen]);

  /** Open the building with the act's form ready. Never a write. */
  const openAct = useCallback((id: string, command: Command, text: string | null) => {
    nonce.current += 1;
    setStart({ facilityId: id, command, nonce: nonce.current });
    setWhy(text ? { id, text } : null);
    update({ open: id });
  }, [update]);
  /** Nothing from an earlier opening survives a close, another building, or a recorded act. */
  const forget = useCallback(() => { setStart(null); setWhy(null); }, []);
  const onAct = useCallback((card: SchedCard, act: string, move: { from: string; to: string | null }) => {
    // A completed visit offers no act (history); nothing opens from it but its building, by a click.
    if (isVisit(card)) return;
    if (act === 'provider' && (move.to === 'confirmed' || move.to === 'upcoming')) {
      const text = `${nameOf(card)}'s visit has no provider, and the server will not confirm a visit without one. Assign a provider first, then Confirm.`;
      setNotice(text);
      openAct(card.id, 'provider', text);
      return;
    }
    setNotice(null);
    openAct(card.id, act as Command, null);
  }, [openAct]);

  const onChanged = useCallback(async (msg: string) => { forget(); onNotice(msg); await load(); }, [forget, onNotice, load]);
  /** What the panel did with the drop's command: said once, and never replayed by a later reopen. */
  const onStartResult = useCallback((name: string) => (r: { kind: 'opened' | 'swapped' | 'refused'; command: Command; opened?: Command; reason?: string }) => {
    setStart(null);
    if (r.kind === 'refused') {
      setWhy(null);
      setNotice(`${COMMAND_LABEL[r.command]} was not opened: ${name}'s plan changed since the board loaded (${r.reason}). The board is read again.`);
      load();
    } else if (r.kind === 'swapped') {
      const text = `${name}'s visit has no provider, and the server will not confirm a visit without one. Assign a provider first, then Confirm.`;
      setNotice(text);
      setWhy((w) => w || { id: '', text });
    }
  }, [load]);

  /** The open building's name when no loaded state card holds it: its lookup, or the completed visit it was opened from. */
  const openName = (): string | null => {
    const id = st.open;
    if (!id) return null;
    if (lookup && lookup.id === id && lookup.card) return buildingName(lookup.card);
    const visits = (columns.find((c) => c.key === COMPLETED_COLUMN)?.cards || []).filter(isVisit);
    const visit = visits.find((k) => k.facility.id === id);
    return visit && visit.facility.name ? visit.facility.name : null;
  };
  // The buildings on the board are the six states' sum (Main table); the history column's visits are not buildings.
  // Main table as the server counts it, so a column left out of the board never shrinks the total; the sum otherwise.
  const mainTab = meta.tabs.find((t) => t.key === 'all');
  const total = mainTab && typeof mainTab.count === 'number' ? mainTab.count : columns.filter((c) => c.key !== COMPLETED_COLUMN).reduce((n, c) => n + c.count, 0);
  const nf = new Intl.NumberFormat('en-US');
  const buckets: ElsewhereBucket[] = meta.tabs.filter((t) => t.key === 'monday-only' || t.key === 'unlinked').map((t) => ({
    key: t.key, label: t.label, count: t.count || 0, what: t.what,
    href: `${pathname}?layout=table&tab=${t.key}`, onSelect: () => setSearchParams(new URLSearchParams({ layout: 'table', tab: t.key }), { replace: true }),
  }));
  const sortOptions = [{ value: '', label: 'Each column’s own order' }, ...Object.keys(SORT_LABEL).filter((k) => !meta.sorts.length || meta.sorts.includes(k)).map((k) => ({ value: k, label: SORT_LABEL[k] }))];

  return (
    <OperationalKanban<SchedCard, SchedKanbanCtx>
      label="Scheduling board"
      idBase="sc-kanban"
      unit={['building', 'buildings']}
      columns={columns}
      renderCard={(card) => <SchedulingCardBody card={card} />}
      cardName={nameOf}
      transitions={SCHEDULING_TRANSITIONS}
      ctx={ctx}
      onAct={onAct}
      onLoadMore={loadMore}
      dragDisabled={!canPlan}
      loading={loading}
      error={err && !columns.length ? err : null}
      onRetry={load}
      notice={err && columns.length ? err : (notice || readErr)}
      emptyText={st.q ? `No building here matches “${st.q}”.` : 'No buildings here.'}
      toolbar={{
        search: { value: st.q, onChange: (q) => update({ q }), placeholder: 'Building name', label: 'Search the board by building' },
        filters: [{
          key: 'provider', label: 'eMerge provider', value: st.filters.provider,
          options: [{ value: '', label: 'Every provider' }, ...((meta.options && meta.options.providers) || []).map((o) => ({ value: o.id, label: `${o.name} (${o.count})` }))],
          onChange: (v) => update({ filters: { provider: v } }),
        }, {
          // `?building=` from the table is honoured here, and can be cleared here.
          key: 'building', label: 'Building', value: st.filters.building,
          options: [{ value: '', label: 'Every building' }, ...((meta.options && meta.options.buildings) || []).map((o) => ({ value: o.id, label: o.name }))],
          onChange: (v) => update({ filters: { building: v } }),
        }],
        sort: { value: sortKey || '', dir: st.dir, options: sortOptions, onChange: (v, d) => update({ sort: v || null, dir: v && d === 'desc' ? 'desc' : 'asc' }) },
        summary: columns.length ? <>{nf.format(total)} buildings on the board{canPlan ? '' : ' · read-only'}</> : null,
        viewSwitch: onLayout ? <LayoutSwitch value="kanban" onChange={onLayout} /> : undefined,
        // "Add a building" on the default screen too (verification F1, 2026-10-14), behind the same gate as the table's:
        // the server's buildings meta (can.create). A new building is eMerge work at once, under "Needs a visit plan".
        actions: <AddBuildingButton meta={buildingMeta} onAdded={(b) => {
          onNotice(`${b.name} added. It is on the board under "Needs a visit plan"; open it to plan a visit or assign coverage.`);
          update({ q: b.name, filters: { provider: '', building: '' }, sort: null });
        }} />,
      }}
      elsewhere={{ label: 'Elsewhere', buckets }}
      detail={{
        openId: st.open,
        onOpen: (id) => { if (st.open !== id) forget(); update({ open: id }); },
        onClose: () => { forget(); update({ open: null }); },
        memoryKey: 'scheduling-kanban',
        // A completed visit opens its BUILDING (2026-10-14): the record is the facility, never the visit plan.
        recordOf: (card) => (isVisit(card) ? card.facility.id : card.id),
        label: (card) => `${card ? buildingName(card) : openName() || 'The open building'}: operations`,
        title: (card) => <strong>{card ? buildingName(card) : openName() || 'Building'}</strong>,
        meta: (card) => (card && !isVisit(card) ? <SchedulingDockMeta card={card} /> : null),
        render: (card) => {
          const id = card ? (isVisit(card) ? card.facility.id : card.id) : st.open;
          if (!id) return null;
          const mine = start && start.facilityId === id ? start : null;
          const looked = lookup && lookup.id === id ? lookup : null;
          const loaded = byId.get(id);
          const known: BuildingCard | null = (card && !isVisit(card) ? card : null) || (loaded && !isVisit(loaded) ? loaded : null) || (looked && looked.card && !isVisit(looked.card) ? looked.card : null);
          const closed = Boolean(known && known.column === 'closed');
          // Not on a loaded page and not answered yet (or not found): the state is unknown, so nothing may be offered.
          const unknown = !known;
          const missing = unknown && Boolean(looked && looked.state === 'missing');
          const note = why && (why.id === id || why.id === '') ? why.text : null;
          const name = known ? buildingName(known) : 'This building';
          return (
            <div className="sck-dock">
              {note && <p className="um-notice" role="note">{note}</p>}
              {closed && known && <p className="um-notice" role="note">{closedNote(closureWords(known))}</p>}
              {unknown && <p className="um-notice" role="status">{missing ? 'This building was not found on the board, so nothing can be done from here.' : 'Checking this building…'}</p>}
              {/* A closed building gets the viewer's real canPlan (2026-10-14): the panel itself offers only Complete and Cancel
                  on a closure (feat/ops-rulings-1014), and the server refuses anything else. The Kanban's closure note above
                  is the one note: readOnlyReason is set for a closed building only when the viewer can plan, to silence
                  the panel's own closure note (shown only when it is unset); with canPlan true it adds no read-only line.
                  A viewer who cannot plan gets no readOnlyReason, so the panel says its own capability line instead. */}
              <FacilityPanelBody facilityId={(card && card.facility && card.facility.id) || id} name={name} row={null} canPlan={canPlan && !unknown} providers={providers} onChanged={onChanged}
                readOnlyReason={unknown ? (missing ? 'Not found on the board: read-only.' : 'Checking this building: read-only until its state is known.') : (closed && known && canPlan ? closedNote(closureWords(known)) : null)}
                buildingMeta={buildingMeta} startWith={mine ? { command: mine.command, nonce: mine.nonce } : null} onStartResult={onStartResult(name)} />
            </div>
          );
        },
      }}
    />
  );
}
